import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import { Avatar } from '../components/Avatar';
import BackButton from '../components/BackButton';
import type { CasePriority, ClientSummary, ClientType } from '../types';

const LEGAL_AREAS: { value: string; key: string }[] = [
  { value: 'דיני משפחה', key: 'family' },
  { value: 'נדל״ן', key: 'real_estate' },
  { value: 'דיני עבודה', key: 'labor' },
  { value: 'מסחרי', key: 'commercial' },
  { value: 'פלילי', key: 'criminal' },
  { value: 'ירושה', key: 'inheritance' },
  { value: 'נזיקין', key: 'tort' },
  { value: 'אחר', key: 'other' },
];

const FEE_TYPES = ['hourly', 'fixed', 'contingency'] as const;
const PRIORITIES: CasePriority[] = ['low', 'medium', 'high', 'urgent'];
const CLIENT_TYPES: { value: ClientType; key: string }[] = [
  { value: 'private', key: 'private' },
  { value: 'business', key: 'business' },
  { value: 'government', key: 'government_short' },
];

type Mode = 'existing' | 'new';

export function NewCase({
  onSaved,
  onCancel,
}: {
  onSaved: (caseId: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['cases', 'clients', 'common']);

  // Client section
  const [mode, setMode] = useState<Mode>('existing');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [results, setResults] = useState<ClientSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<ClientSummary | null>(null);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newType, setNewType] = useState<ClientType>('private');

  // Case section
  const [area, setArea] = useState('');
  const [feeType, setFeeType] = useState<'hourly' | 'fixed' | 'contingency'>('hourly');
  const [hourlyRate, setHourlyRate] = useState('');
  const [procedureType, setProcedureType] = useState('');
  const [court, setCourt] = useState('');
  const [dueAt, setDueAt] = useState('');
  const [priority, setPriority] = useState<CasePriority>('medium');
  const [description, setDescription] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounce search input
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 200);
    return () => clearTimeout(id);
  }, [search]);

  // Load + filter clients in existing-client mode. Empty query → most
  // recent 20 (the default list), so the user sees their roster as a
  // dropdown without having to type first.
  const reqId = useRef(0);
  useEffect(() => {
    if (mode !== 'existing') {
      setResults([]);
      setSearching(false);
      return;
    }
    const myReq = ++reqId.current;
    setSearching(true);
    (async () => {
      try {
        const url = debounced
          ? `/api/clients?q=${encodeURIComponent(debounced)}`
          : '/api/clients';
        const data = await apiFetch<{ items: ClientSummary[] }>(url);
        if (myReq !== reqId.current) return;
        setResults(data.items);
      } catch {
        if (myReq !== reqId.current) return;
        setResults([]);
      } finally {
        if (myReq === reqId.current) setSearching(false);
      }
    })();
  }, [debounced, mode]);

  const previewClientName = useMemo(() => {
    if (mode === 'existing') return picked?.name ?? '';
    return `${newFirstName} ${newLastName}`.trim();
  }, [mode, picked, newFirstName, newLastName]);

  function pickClient(c: ClientSummary) {
    setPicked(c);
    setSearch('');
    setResults([]);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Validate client section
    if (mode === 'existing' && !picked) {
      setError(t('cases:wizard.error_pick_client'));
      return;
    }
    if (mode === 'new') {
      const fullName = `${newFirstName} ${newLastName}`.trim();
      if (!fullName) {
        setError(t('cases:wizard.error_client_name'));
        return;
      }
    }

    setBusy(true);
    try {
      const body: Record<string, unknown> = {
        area: area || null,
        feeType,
        hourlyRateIls: hourlyRate ? Number(hourlyRate) : null,
        procedureType: procedureType.trim() || null,
        court: court.trim() || null,
        dueAt: dueAt ? new Date(dueAt).toISOString() : null,
        priority,
        description: description.trim() || null,
      };
      if (mode === 'existing') {
        body.clientId = picked!.id;
      } else {
        body.newClient = {
          type: newType,
          name: `${newFirstName} ${newLastName}`.trim(),
          phone: newPhone.trim() || null,
          email: newEmail.trim() || null,
        };
      }
      const created = await apiFetch<{ id: string }>('/api/cases', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      onSaved(created.id);
    } catch (err) {
      if (err instanceof ApiError) {
        const errorKey = `clients:errors.${err.code}`;
        const translated = t(errorKey, { defaultValue: '' });
        setError(translated || t('common:errors.code', { code: err.code }));
      } else {
        setError(err instanceof Error ? err.message : t('common:errors.generic'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="form-wrap">
      <BackButton onClick={onCancel} />
      <form className="form-card" onSubmit={handleSubmit}>
        <div className="form-card-header">
          <div>
            <div className="form-preview-name">{t('cases:wizard.title')}</div>
            {previewClientName && (
              <div className="form-preview-type">
                {t('cases:form.for_client', { clientName: previewClientName })}
              </div>
            )}
          </div>
        </div>

        {/* Client section */}
        <div className="form-section">
          <div className="section-label">{t('cases:wizard.section_client')}</div>
          <div className="type-row">
            <div
              className={`type-btn ${mode === 'existing' ? 'active' : ''}`}
              onClick={() => {
                setMode('existing');
                setError(null);
              }}
            >
              {t('cases:wizard.tab_existing')}
            </div>
            <div
              className={`type-btn ${mode === 'new' ? 'active' : ''}`}
              onClick={() => {
                setMode('new');
                setPicked(null);
                setError(null);
              }}
            >
              {t('cases:wizard.tab_new')}
            </div>
          </div>

          {mode === 'existing' && !picked && (
            <>
              <div className="field" style={{ marginTop: 12 }}>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t('cases:wizard.search_placeholder')}
                  autoFocus
                />
              </div>
              <div className="full-panel" style={{ marginTop: 8 }}>
                {searching ? (
                  <div className="empty">{t('common:actions.loading_dots')}</div>
                ) : results.length === 0 ? (
                  <div className="empty">
                    {debounced
                      ? t('cases:wizard.search_empty')
                      : t('cases:wizard.no_clients_yet')}
                  </div>
                ) : (
                  results.map((c) => (
                    <div
                      key={c.id}
                      className="case-row"
                      onClick={() => pickClient(c)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Avatar name={c.name} size="md" />
                      <div className="case-info">
                        <div className="case-name">{c.name}</div>
                        <div className="case-sub">
                          {[c.phone, c.email].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {mode === 'existing' && picked && (
            <div className="case-row" style={{ marginTop: 12 }}>
              <Avatar name={picked.name} size="md" />
              <div className="case-info">
                <div className="case-name">{picked.name}</div>
                <div className="case-sub">
                  {[picked.phone, picked.email].filter(Boolean).join(' · ') || '—'}
                </div>
              </div>
              <button type="button" className="btn-cancel" onClick={() => setPicked(null)}>
                {t('cases:wizard.change_client')}
              </button>
            </div>
          )}

          {mode === 'new' && (
            <div className="field-grid" style={{ marginTop: 12 }}>
              <div className="field">
                <label>{t('cases:wizard.field_first_name')} <span className="req">*</span></label>
                <input
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label>{t('cases:wizard.field_last_name')} <span className="req">*</span></label>
                <input
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label>{t('cases:wizard.field_phone')}</label>
                <input
                  type="tel"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                />
              </div>
              <div className="field">
                <label>{t('cases:wizard.field_email')}</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
              </div>
              <div className="field">
                <label>{t('cases:wizard.field_type')}</label>
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value as ClientType)}
                >
                  {CLIENT_TYPES.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {t(`clients:types.${opt.key}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Case section */}
        <div className="form-section">
          <div className="section-label">{t('cases:wizard.section_case')}</div>
          <div className="field-grid">
            <div className="field">
              <label>{t('cases:form.field_area')}</label>
              <select value={area} onChange={(e) => setArea(e.target.value)}>
                <option value="">{t('common:actions.select')}</option>
                {LEGAL_AREAS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {t(`cases:areas.${a.key}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>{t('cases:form.field_procedure_type')}</label>
              <input
                value={procedureType}
                onChange={(e) => setProcedureType(e.target.value)}
                placeholder={t('cases:form.field_procedure_type_placeholder')}
              />
            </div>
            <div className="field">
              <label>{t('cases:form.field_court')}</label>
              <input
                value={court}
                onChange={(e) => setCourt(e.target.value)}
                placeholder={t('cases:form.field_court_placeholder')}
              />
            </div>
            <div className="field">
              <label>{t('cases:form.field_priority')}</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value as CasePriority)}>
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {t(`cases:priorities.${p}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>{t('cases:form.field_due_at')}</label>
              <input
                type="date"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
                min="1900-01-01"
                max="2100-12-31"
              />
            </div>
            <div className="field">
              <label>{t('cases:form.field_fee_type')}</label>
              <select
                value={feeType}
                onChange={(e) => setFeeType(e.target.value as 'hourly' | 'fixed' | 'contingency')}
              >
                {FEE_TYPES.map((f) => (
                  <option key={f} value={f}>
                    {t(`cases:fee_types.${f}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>{t('cases:form.field_hourly_rate')}</label>
              <input
                type="number"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                placeholder={t('cases:form.field_hourly_rate_placeholder')}
                min={0}
                step={50}
              />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>{t('cases:form.field_description')}</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder={t('cases:form.field_description_placeholder')}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="form-section">
            <div className="signin-error">{error}</div>
          </div>
        )}

        <div className="form-footer">
          <button type="submit" className="btn-save" disabled={busy}>
            {busy ? t('common:actions.loading_dots') : t('cases:wizard.submit')}
          </button>
          <button type="button" className="btn-cancel" onClick={onCancel} disabled={busy}>
            {t('common:actions.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}
